<template>
    <section>
        <div v-if="emptyHistory" class="tx-history-empty-panel" v-text="$t('address.tx_table.empty')" />

        <div v-show="!emptyHistory" class="tx-history-wrap desktop-table">
            <table class="tx-table">
                <thead v-if="!isMobile">
                    <tr>
                        <th width="100">
                            <div class="tx-table__cell" v-text="$t('address.tx_table.age')" />
                        </th>
                        <th width="100">
                            <div class="tx-table__cell" v-text="$t('address.tx_table.event')" />
                        </th>
                        <th>
                            <div class="tx-table__cell" v-text="$t('address.tx_table.address')" />
                        </th>
                        <th class="column-wide">
                            <div class="tx-table__cell tx-table__cell--align-right" style="padding-right: 26px;"
                                v-text="$t('address.tx_table.value')" />
                        </th>
                        <th v-pre width="10"></th>
                    </tr>
                </thead>

                <template v-if="address">
                    <template v-for="tx in pendingTransactions">
                        <component
                            v-for="(msg, idx) in tx.messages"
                            v-bind:key="`pending_tx_${tx.hash}_msg_${idx}`"
                            v-bind:is="isMobile ? 'tx-row-mobile' : 'tx-row-simple'"
                            v-bind:class="{ 'sub-list': idx > 0 }"
                            v-bind:address="address"
                            v-bind:txHash="tx.hash"
                            v-bind:txLt="tx.lt"
                            v-bind:timestamp="tx.timestamp"
                            v-bind:fee="tx.fee"
                            v-bind:exitCode="tx.exit_code"
                            v-bind:outputCount="tx.output_count"
                            v-bind:action="msg.action"
                            v-bind:event="msg.event"
                            v-bind:meta="msg.meta"
                            v-bind:hideIcon="hideIcon"
                            v-bind="msg"
                            v-bind:msg="msg"
                            v-bind:encrypted="msg.encrypted"
                            v-bind:finality="msg.finality"
                        />
                    </template>
                    <template v-for="tx in allTransactions">
                        <keep-alive
                            v-for="(msg, idx) in tx.messages"
                            v-bind:key="`tx_${tx.hash}_msg_${idx}`">
                            <component
                                v-if="displayMsg(msg)"
                                v-bind:is="isMobile ? 'tx-row-mobile' : 'tx-row-simple'"
                                v-bind:class="{ 'sub-list': idx > 0 }"
                                v-bind:address="address"
                                v-bind:txHash="tx.hash"
                                v-bind:txLt="tx.lt"
                                v-bind:timestamp="tx.timestamp"
                                v-bind:fee="tx.fee"
                                v-bind:exitCode="tx.exit_code"
                                v-bind:outputCount="tx.output_count"
                                v-bind:action="msg.action"
                                v-bind:event="msg.event"
                                v-bind:meta="msg.meta"
                                v-bind:hideIcon="hideIcon"
                                v-bind="msg"
                                v-bind:msg="msg"
                                v-bind:encrypted="msg.encrypted"
                            />
                        </keep-alive>
                    </template>
                </template>

                <template v-if="!address || transactions.length === 0">
                    <component
                        v-bind:is="isMobile
                            ? 'tx-row-skeleton-mobile'
                            : 'tx-row-skeleton-simple'"
                        v-for="i in 8"
                        v-bind:key="`tx_skeleton_${i}`"
                        v-bind:hide-icon="hideIcon"/>
                </template>
            </table>
        </div>

        <ui-mugen-scroll
            v-bind:handler="loadMore"
            v-bind:shouldHandle="shouldHandleScroll"
            v-bind:showButton="showPreloader"
            v-bind:isLoading="isLoading"/>
    </section>
</template>

<script>
import { mapState } from 'vuex';
import { getToncenterActions } from '~/api/toncenterV3.js';
import { closeWebsocketConnection, startTxAndStateWebsocket, stopTxAndStateWebsockets } from '~/api/websocket.js';
import TxRowSkeletonSimple from './TxRowSkeletonSimple.vue';
import TxRowSkeletonMobile from './TxRowSkeletonMobile.vue';
import TxRowSimple from './TxRowSimple.vue';
import TxRowMobile from './TxRowMobile.vue';

export default {
    props: {
        address: {
            type: String,
            required: false,
        },
    },

    data() {
        return {
            transactions: [],
            isLoading: true,
            hasMore: true,
            emptyHistory: false,
            lastActivity: undefined,
            hideIcon: false,
        };
    },

    computed: {
        allTransactions() {
            return [...this.newTransactions, ...this.transactions];
        },

        shouldHandleScroll() {
            return !this.isLoading && this.address && this.hasMore && this.transactions.length > 0;
        },

        showPreloader() {
            return this.address && this.transactions.length > 0 && this.hasMore;
        },

        txView() {
            return this.$store.state.transactionViewData;
        },

        ...mapState({ 
            dataSource: 'txTableSource',
            newTransactions: 'websocketTransactions',
            pendingTransactions: 'websocketPendingTransactions',
        }),
    },

    watch: {
        address: {
            immediate: true,
            handler: 'resetData',
        },

        dataSource: {
            immediate: false,
            handler() {
                this.resetData();
                this.loadData();
            },
        },
    },

    activated() {
        this.emitLastActivity();
    },

    methods: {
        resetData() {
            if (!this.address) return;

            this.transactions = [];
            this.isLoading = true;
            this.hasMore = true;
            this.emptyHistory = false;

            stopTxAndStateWebsockets();

            this.$nextTick(this.loadData);
        },

        emitLastActivity() {
            this.$emit('lastActivityUpdate', this.lastActivity);
        },

        displayMsg(msg) {
            return msg?.event && !msg.is_external;
        },

        async loadData() {
            this.isLoading = true;
            const limit = 20;

            startTxAndStateWebsocket(this.address);

            try {
                const transactions = await getToncenterActions(this.address, { limit });

                this.transactions = transactions;
                this.emptyHistory = !transactions.length;
                this.hasMore = transactions.length >= limit;
                this.lastActivity = transactions.length ? transactions[0]?.timestamp : null;

                this.emitLastActivity();
            } catch (error) {
                console.error('Error loading transactions:', error);
            } finally {
                this.isLoading = false;
            }
        },

        async loadMore() {
            if (this.isLoading || !this.hasMore) return;

            this.isLoading = true;
            const limit = 50;

            try {
                const newTx = await getToncenterActions(this.address, {
                    limit,
                    offset: this.transactions.length,
                });

                this.hasMore = newTx.length >= limit;
                this.transactions = [...this.transactions, ...newTx];
            } catch (error) {
                console.error('Error loading more transactions:', error);
            } finally {
                this.isLoading = false;
            }
        },
    },

     beforeDestroy() {
        closeWebsocketConnection();
    },

    components: {
        TxRowSimple, TxRowMobile, TxRowSkeletonMobile, TxRowSkeletonSimple,
    },
};
</script>
